import React from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/modules/auth/context/AuthContext'
import {
  fetchCommunityThreadNotificationsEnabled,
  setCommunityThreadNotificationsEnabled,
  type CommunityThreadContextType,
} from '../services/communityThreadNotificationsService'
import { subscribeCommunityThreadNotificationsAutoEnabled } from '../services/communityThreadNotificationsEvents'

type UseCommunityThreadNotificationsParams = {
  contextType: CommunityThreadContextType
  contextId: number | null
  active: boolean
}

export function useCommunityThreadNotifications({
  contextType,
  contextId,
  active,
}: UseCommunityThreadNotificationsParams) {
  const { user } = useAuth()
  const userId = user?.id ? String(user.id) : null
  const safeContextId =
    contextId != null && Number.isFinite(Number(contextId)) && Number(contextId) > 0
      ? Math.floor(Number(contextId))
      : null

  const [enabled, setEnabled] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const refresh = React.useCallback(async () => {
    if (!active || !safeContextId || !userId) {
      setEnabled(false)
      setError(null)
      return
    }

    setLoading(true)
    try {
      const val = await fetchCommunityThreadNotificationsEnabled({
        contextType,
        contextId: safeContextId,
      })
      setEnabled(val)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [active, contextType, safeContextId, userId])

  React.useEffect(() => {
    void refresh()
  }, [refresh])

  React.useEffect(() => {
    if (!active || !userId) return

    const ch = supabase
      .channel(`realtime:community_thread_subscriptions:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'community_thread_subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload: any) => {
          try {
            const row = payload?.new || payload?.record || payload?.old || null
            if (!row) return
            if (String(row.context_type || '') !== contextType) return
            if (safeContextId == null || Number(row.context_id) !== safeContextId) return

            if (payload?.eventType === 'DELETE') {
              setEnabled(false)
              return
            }

            setEnabled(Boolean(row.enabled))
          } catch {
            // ignorar
          }
        },
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(ch)
      } catch {
        // ignore
      }
    }
  }, [active, contextType, safeContextId, userId])

  React.useEffect(() => {
    if (!active || !userId || safeContextId == null) return

    return subscribeCommunityThreadNotificationsAutoEnabled((detail) => {
      if (detail.contextType !== contextType) return
      if (detail.contextId !== safeContextId) return
      setEnabled(true)
    })
  }, [active, contextType, safeContextId, userId])

  const updateEnabled = React.useCallback(
    async (nextEnabled: boolean): Promise<boolean | null> => {
      if (!safeContextId || !userId) return null

      setSaving(true)
      try {
        const val = await setCommunityThreadNotificationsEnabled({
          contextType,
          contextId: safeContextId,
          enabled: nextEnabled,
        })
        setEnabled(Boolean(val))
        setError(null)
        return Boolean(val)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        return null
      } finally {
        setSaving(false)
      }
    },
    [contextType, safeContextId, userId],
  )

  return {
    enabled,
    loading,
    saving,
    error,
    refresh,
    setEnabled: updateEnabled,
  }
}
